import {
    Avatar,
    Box,
    HStack,
    Progress,
    Text,
    VStack,
    Badge,
    Spinner,
} from "@chakra-ui/react";
import { Camera, Plus, PlusSquare } from "lucide-react";
import {
    useRef,
    useState,
} from "react";
import { useDispatch } from "react-redux";

import Card from "../../Dashboard/components/Card";
import {
    updateDriverPartnerAction,
    fetchMyDriverPartnerAction,
} from "../../../state/actions/driverPartnerActions";
import { toaster } from "../../../components/toaster";
import ActivityToast from "../../../components/ActivityToast";

const ProfileSummary = ({
    driverPartner,
}) => {
    const dispatch =
        useDispatch();

    const imageInputRef =
        useRef(null);

    const [
        updatingImage,
        setUpdatingImage,
    ] = useState(false);

    const user =
        driverPartner?.user;

    const fullName =
        [
            user?.first_name,
            user?.middle_name,
            user?.last_name,
        ]
            .filter(
                value =>
                    value?.trim()
            )
            .join(" ");

    const displayName =
        fullName ||
        user?.username ||
        "Driver Partner";

    const initials =
        displayName
            .split(" ")
            .map(
                name =>
                    name[0]
            )
            .join("")
            .slice(0, 2)
            .toUpperCase();

    const profileImage =
        driverPartner?.documents?.find(
            document =>
                document.document_type ===
                "Profile Image"
        );

    const API_URL =
        process.env.REACT_APP_API_BASE_URL?.replace(
            /\/$/,
            ""
        );

    const profileImageUrl =
        profileImage?.file_path
            ? `${API_URL}/${profileImage.file_path.replace(
                  /^\//,
                  ""
              )}`
            : null;

    const joinedDate =
        driverPartner?.created_at
            ? new Date(
                  driverPartner.created_at
              ).toLocaleDateString(
                  "en-US",
                  {
                      month:
                          "long",
                      year:
                          "numeric",
                  }
              )
            : "-";

    const requiredFields = [
        user?.first_name,
        user?.last_name,
        user?.phone_number,
        user?.email,
        user?.district,
        user?.village,
        user?.physical_address,
        user?.postal_address,
        user?.id_or_passport_number,
    ];

    const completedFields =
        requiredFields.filter(
            field =>
                field &&
                String(field).trim() !==
                    ""
        ).length;

    const profileCompletion =
        Math.round(
            (completedFields /
                requiredFields.length) *
                100
        );

    const handleProfileImageChange =
        async e => {
            const file =
                e.target.files?.[0];

            if (!file) return;

            setUpdatingImage(true);

            const result =
                await dispatch(
                    updateDriverPartnerAction(
                        driverPartner.driver_partner_id,
                        {
                            profile_image:
                                file,
                        }
                    )
                );

            setUpdatingImage(false);

            if (
                result?.success
            ) {
                toaster.create({
                    render: () => (
                        <ActivityToast
                            type="success"
                            title="Profile Updated"
                            description="Profile image changed successfully."
                            timestamp="Just now"
                        />
                    ),
                });

                dispatch(
                    fetchMyDriverPartnerAction()
                );
            } else {
                toaster.create({
                    render: () => (
                        <ActivityToast
                            type="error"
                            title="Update Failed"
                            description={
                                result?.error ||
                                "Unable to update profile image."
                            }
                            timestamp="Just now"
                        />
                    ),
                });
            }

            e.target.value = "";
        };

    return (
        <>
            <input
                ref={
                    imageInputRef
                }
                type="file"
                accept="image/*"
                hidden
                onChange={
                    handleProfileImageChange
                }
            />

            <Card>
                <HStack
                    align="flex-start"
                    spacing={5}
                >
                    <Box
                        position="relative"
                    >
                        <Avatar.Root
                            size="2xl"
                            borderRadius="16px"
                        >
                            <Avatar.Image
                                borderRadius="16px"
                                src={
                                    profileImageUrl
                                }
                            />

                            <Avatar.Fallback
                                bg="gray.200"
                            >
                                {null}
                            </Avatar.Fallback>
                        </Avatar.Root>
                        <Box
                            position="absolute"
                            bottom="-4px"
                            right="-4px"
                            w="20px"
                            h="20px"
                            borderRadius="full"
                            bg="white"
                            border="2px solid white"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            cursor="pointer"
                            boxShadow="0 2px 8px rgba(0,0,0,0.15)"
                            transition="all 0.2s"
                            _hover={{
                                transform: "scale(1.08)",
                            }}
                            onClick={() =>
                                imageInputRef.current?.click()
                            }
                        >
                            {updatingImage ? (
                                <Spinner
                                    size="xs"
                                    color="#2453FF"
                                />
                            ) : (
                                <Plus
                                    size={15}
                                    color="#2453FF"
                                    strokeWidth={2.4}
                                />
                            )}
                        </Box>
                                        </Box>

                    <VStack
                        align="stretch"
                        flex="1"
                        gap={3}
                    >
                        <HStack
                            justify="space-between"
                            flexWrap="wrap"
                        >
                            <Box>
                                <Text
                                    fontSize="2xl"
                                    fontWeight="700"
                                    color="#071D54"
                                >
                                    {
                                        displayName
                                    }
                                </Text>

                                <Text
                                    mt={1}
                                    color="gray.500"
                                    fontSize="sm"
                                >
                                    Partner
                                    ID:{" "}
                                    {
                                        driverPartner?.partner_referral_code
                                    }{" "}
                                    • Joined{" "}
                                    {
                                        joinedDate
                                    }
                                </Text>
                            </Box>

                            <Badge
    display="inline-flex"
    alignItems="center"
    gap={2}
    px={4}
    py={2}
    borderRadius="full"
    fontWeight="600"
    fontSize="sm"
    colorPalette={
        driverPartner?.status === "Approved"
            ? "green"
            : driverPartner?.status === "Pending"
            ? "yellow"
            : driverPartner?.status === "Review Required"
            ? "orange"
            : driverPartner?.status === "Rejected"
            ? "red"
            : "gray"
    }
>
    <Box
        w="7px"
        h="7px"
        borderRadius="full"
        bg={
            driverPartner?.status === "Approved"
                ? "green.500"
                : driverPartner?.status === "Pending"
                ? "yellow.500"
                : driverPartner?.status === "Review Required"
                ? "orange.500"
                : driverPartner?.status === "Rejected"
                ? "red.500"
                : "gray.500"
        }
    />

    {driverPartner?.status || "Unknown"}
</Badge>
                        </HStack>

                        <Box pt={2}>
                            <HStack
                                justify="space-between"
                                mb={2}
                            >
                                <Text
                                    fontSize="sm"
                                    color="gray.500"
                                >
                                    Profile
                                    Completion
                                </Text>

                                <Text
                                    fontSize="sm"
                                    color="#2453FF"
                                    fontWeight="700"
                                >
                                    {
                                        profileCompletion
                                    }
                                    %
                                </Text>
                            </HStack>

                            <Progress.Root
                                value={
                                    profileCompletion
                                }
                                size="sm"
                                borderRadius="full"
                            >
                                <Progress.Track bg="#E8EEFF">
                                    <Progress.Range bg="#2453FF" />
                                </Progress.Track>
                            </Progress.Root>
                        </Box>
                    </VStack>
                </HStack>
            </Card>
        </>
    );
};

export default ProfileSummary;